import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query Client Configuration
 *
 * Platform-wide caching strategy to preserve data across navigation:
 * - 10 minute staleTime: Data considered fresh for 10 minutes (prevents refetches on navigation)
 * - 30 minute gcTime: Unused data stays in cache for 30 minutes (survives tab switches)
 * - No refetch on window focus: Prevents unnecessary refetches
 * - No refetch on mount: Uses cached data when navigating back to a page
 * - No refetch on reconnect: Don't refetch when network reconnects
 *
 * Key Insight: Most data loss happens because pages use useState instead of useQuery.
 * Pages should use useQuery for ALL server data and usePersistedState for form/filter state.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // How long data is considered fresh (10 minutes)
      // Increased to prevent refetches during normal navigation
      staleTime: 1000 * 60 * 10,

      // How long unused data stays in cache (30 minutes)
      // Increased to keep data available longer when switching between pages
      gcTime: 1000 * 60 * 30,

      // Don't refetch on window focus (prevents data loss on alt-tab)
      refetchOnWindowFocus: false,

      // Don't refetch when component mounts (use cached data)
      // This is KEY for preserving data when navigating back to a page
      refetchOnMount: false,

      // Don't refetch when network reconnects
      refetchOnReconnect: false,

      // Retry failed requests once
      retry: 1,

      // Keep previous data while fetching new data
      // This prevents the "flash of empty state" when data is loading
      placeholderData: (previousData: unknown) => previousData,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
});
